import React from "react";
import { Box, Typography, Paper, Skeleton } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  School as SchoolIcon,
  CameraOutdoor as CampIcon,
  AccessTime as ClockIcon,
} from "@mui/icons-material";
import LinearProgress from "@mui/material/LinearProgress";

// ─── Styled Components ───────────────────────────────────────────────────────

const SectionContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1.5),
  backgroundColor: "#ffffff",
  border: "1px solid rgba(0, 136, 204, 0.08)",
  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
  marginBottom: theme.spacing(3),
  "&:hover": { boxShadow: "0 6px 20px rgba(0, 0, 0, 0.1)" },
  transition: "box-shadow 0.2s ease-in-out",
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  borderBottom: "2px solid #f0f0f0",
  gap: theme.spacing(1),
  flexWrap: "wrap",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const LimitCard = styled(Paper)(({ theme, accent }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.2),
  border: `1px solid ${accent}30`,
  backgroundColor: `${accent}0a`,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  transition: "all 0.25s ease-in-out",
  position: "relative",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 6px 20px ${accent}30`,
    borderColor: `${accent}50`,
  },
}));

const IconBox = styled(Box)(({ accent }) => ({
  width: 38,
  height: 38,
  borderRadius: 8,
  backgroundColor: accent,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  "& .MuiSvgIcon-root": { color: "#fff", fontSize: "1.1rem" },
}));

const StyledLinearProgress = styled(LinearProgress)(({ barcolor }) => ({
  height: 7,
  borderRadius: 4,
  backgroundColor: `${barcolor}25`,
  "& .MuiLinearProgress-bar": { backgroundColor: barcolor, borderRadius: 4 },
}));

// ─── Helpers ─────────────────────────────────────────────────────────────────

function accentFromRatio(used, total) {
  if (total === 0) return "#9e9e9e";
  const pct = used / total;
  if (pct >= 1) return "#e53935";
  if (pct >= 0.8) return "#fb8c00";
  return "#43a047";
}

function fmtDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}


// ─── Single limit card (FDP) ─────────────────────────────────────────────────

function LimitItem({ icon: Icon, label, total, used, left, extraInfo }) {
  const accent = accentFromRatio(used, total);
  const pct = total > 0 ? Math.round((used / total) * 100) : 0;

  return (
    <LimitCard accent={accent} elevation={0}>
      {/* Header row — label left, red expiry badge right */}
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
        <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
          <IconBox accent={accent}>
            <Icon />
          </IconBox>
          <Typography fontSize="0.78rem" fontWeight={600} color="text.secondary" noWrap>
            {label}
          </Typography>
        </Box>

        {/* Red expiry badge — top-right */}
        {extraInfo && (
          <Box
            display="flex"
            alignItems="center"
            gap={0.4}
            sx={{
              background: "rgba(229,57,53,0.08)",
              border: "1px solid rgba(229,57,53,0.25)",
              borderRadius: "6px",
              px: 0.9,
              py: 0.35,
              flexShrink: 0,
            }}
          >
            <ClockIcon sx={{ fontSize: "0.65rem", color: "#e53935" }} />
            <Typography fontSize="0.6rem" color="#c62828" fontWeight={600} whiteSpace="nowrap">
              {extraInfo}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Bottom section — stat row + progress bar, pinned to bottom */}
      <Box display="flex" flexDirection="column" gap={1.2}>
        {/* Stat row: Available, Used, Total */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography fontSize="1.25rem" fontWeight={700} color={accent} lineHeight={1.1}>
              {left}
            </Typography>
            <Typography fontSize="0.68rem" fontWeight={500} color="text.secondary" mt={0.2}>
              Available
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography fontSize="1.25rem" fontWeight={700} color="#e65100" lineHeight={1.1}>
              {used}
            </Typography>
            <Typography fontSize="0.68rem" fontWeight={500} color="text.secondary" mt={0.2}>
              Used
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography fontSize="1.25rem" fontWeight={700} color="text.primary" lineHeight={1.1}>
              {total}
            </Typography>
            <Typography fontSize="0.68rem" fontWeight={500} color="text.secondary" mt={0.2}>
              Total
            </Typography>
          </Box>
        </Box>

        {/* Progress bar */}
        <StyledLinearProgress
          variant="determinate"
          value={Math.min(pct, 100)}
          barcolor={accent}
        />
      </Box>
    </LimitCard>
  );
}

// ─── Tech Camp limit card ─────────────────────────────────────────────────────

function TechCampSlotCard({ total, used, left, expiryDate }) {
  return (
    <LimitItem
      icon={CampIcon}
      label="Tech Camp Limit"
      total={total}
      used={used}
      left={left}
      extraInfo={expiryDate ? `Expires ${fmtDate(expiryDate)}` : null}
    />
  );
}


// ─── Skeleton ─────────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <Paper
      elevation={0}
      sx={{ p: 2, borderRadius: 1.5, border: "1px solid #e0e0e0", display: "flex", flexDirection: "column", gap: 1 }}
    >
      <Box display="flex" gap={1.5} alignItems="center">
        <Skeleton variant="rounded" width={38} height={38} />
        <Skeleton variant="text" width="55%" />
      </Box>
      <Skeleton variant="text" width="40%" height={36} />
      <Skeleton variant="rounded" height={7} />
    </Paper>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const LimitsSection = ({ limitsData, loading, onBookNow }) => {
  if (loading) {
    return (
      <SectionContainer elevation={0}>
        <SectionHeader>
          <SectionTitle variant="body2">FDP &amp; Tech Camp Limits</SectionTitle>
          <Skeleton variant="rounded" width={110} height={24} sx={{ borderRadius: "6px" }} />
        </SectionHeader>
        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
          <SkeletonCard />
          <SkeletonCard />
        </Box>
      </SectionContainer>
    );
  }

  if (!limitsData) return null;

  // FDP
  const fdp = limitsData.fdp ?? {};
  const educator = fdp.educator_limit ?? { total: 0, used: 0, left: 0 };
  const fdpHost = fdp.host_limit ?? { total: 0, used: 0, left: 0 };
  const fdpExpiry = fdp.expired_date ?? null;

  // Tech Camp
  const techCamp = limitsData.tech_camp ?? {};
  const techCampLimit = techCamp.techcamp_limit ?? techCamp.host_limit ?? {
    total: techCamp.total ?? 0,
    used: techCamp.used ?? 0,
    left: techCamp.left ?? 0,
  };
  const techCampTotal = techCamp.total ?? techCampLimit.total ?? 0;
  const techCampUsed = techCamp.used ?? techCampLimit.used ?? 0;
  const techCampLeft = techCamp.left ?? techCampLimit.left ?? Math.max(0, techCampTotal - techCampUsed);
  const techCampExpiry = techCamp.expired_date ?? null;

  // Hide the entire section if there's no FDP educator limits and no Tech Camp limits
  if (educator.total === 0 && techCampTotal === 0) return null;

  // Single expiry date chip for the two limits
  const expiryDate = fdpExpiry || techCampExpiry;

  const cardCount = (educator.total > 0 ? 1 : 0) + (techCampTotal > 0 ? 1 : 0);

  return (
    <SectionContainer elevation={0}>
      <SectionHeader>
        <SectionTitle variant="body2">FDP &amp; Tech Camp Limits</SectionTitle>
        {expiryDate && (
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{
              background: "rgba(229,57,53,0.08)",
              border: "1px solid rgba(229,57,53,0.25)",
              borderRadius: "6px",
              px: 1,
              py: 0.35,
              flexShrink: 0,
            }}
          >
            <ClockIcon sx={{ fontSize: "0.75rem", color: "#e53935" }} />
            <Typography
              fontSize="0.7rem"
              color="#c62828"
              fontWeight={600}
              whiteSpace="nowrap"
            >
              Expires {fmtDate(expiryDate)}
            </Typography>
          </Box>
        )}
      </SectionHeader>

      <Box
        display="grid"
        gridTemplateColumns={{
          xs: "1fr",
          sm: cardCount > 1 ? "repeat(2, 1fr)" : "1fr",
        }}
        gap={2}
      >
        {/* FDP Host Limit */}
        {/* {fdpHost.total > 0 && (
          <LimitItem
            icon={SchoolIcon}
            label="FDP Host Limit"
            total={fdpHost.total}
            used={fdpHost.used}
            left={fdpHost.left}
            extraInfo={showFdpExpiry ? `Expires ${fmtDate(showFdpExpiry)}` : null}
          />
        )} */}

        {/* FDP Educator Limit */}
        {educator.total > 0 && (
          <LimitItem
            icon={SchoolIcon}
            label="FDP Educator Limit"
            total={educator.total}
            used={educator.used}
            left={educator.left}
          />
        )}

        {/* Tech Camp Limit */}
        {techCampTotal > 0 && (
          <LimitItem
            icon={CampIcon}
            label="Tech Camp Limit"
            total={techCampTotal}
            used={techCampUsed}
            left={techCampLeft}
          />
        )}
      </Box>
    </SectionContainer>
  );
};

export default LimitsSection;
